import api from './api';

// Tipos para Servicios y Categorías de Servicios
export interface Service {
  id: number;
  name: string;
  description?: string;
  duration_minutes: number;
  price: number;
  category: number; // Se envía el ID de la categoría
  professionals?: number[]; // IDs de professionals asignados
  professionals_detail?: Professional[]; // Objetos expandidos (read-only)
}

export interface ServiceCategory {
  id: number;
  name: string;
  image?: string;
  services: Service[];
}

export interface Professional {
  id: number;
  first_name: string;
  last_name: string;
  full_name?: string;
  name?: string; // Para compatibilidad
  email?: string;
  phone_number?: string;
  specialty?: string; // Agregar campo specialty
  image?: string | null; // URL de la imagen
}

export interface Appointment {
  id: number;
  professional?: number;
  professional_detail?: Professional;
  date: string;
  start_time: string;
  end_time: string;
  services?: number[];
  appointment_services?: any[];
  total_duration: number;
  total_price: number;
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  created_at: string;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';
  status_updated_at?: string;
  booking_source?: 'online' | 'manual';
  has_deposit?: boolean;
  deposit_amount?: number;
}

// Objeto del servicio para interactuar con la API de appointments
export const appointmentService = {
  // --- CATEGORÍAS DE SERVICIOS ---

  // Obtener todas las categorías con sus servicios anidados
  getAllCategories: async (): Promise<ServiceCategory[]> => {
    const response = await api.get('appointments/categories/');
    const data = response.data;
    // Normalizar formatos: puede venir paginado { results: [...] } o como lista directa
    if (data == null) return [];
    if (Array.isArray(data)) return data as ServiceCategory[];
    if (Array.isArray(data.results)) return data.results as ServiceCategory[];
    // Fallback: si es un objeto individual, devolverlo en arreglo
    return [data] as ServiceCategory[];
  },

  // Crear una nueva categoría de servicio
  createCategory: async (categoryData: { name: string; image?: string }): Promise<ServiceCategory> => {
    const response = await api.post('appointments/categories/', categoryData);
    return response.data;
  },

  // Actualizar una categoría de servicio
  updateCategory: async (id: number, categoryData: { name?: string; image?: string | null }): Promise<ServiceCategory> => {
    console.log('[appointmentService.updateCategory] Enviando:', id, categoryData);
    const response = await api.patch(`appointments/categories/${id}/`, categoryData);
    console.log('[appointmentService.updateCategory] Respuesta:', response.data);
    return response.data;
  },

  // Eliminar una categoría de servicio
  deleteCategory: async (id: number): Promise<void> => {
    await api.delete(`appointments/categories/${id}/`);
  },

  // --- CATEGORÍAS SANTIAGO ---

  // Crear una nueva categoría de servicio en Santiago
  createCategorySantiago: async (categoryData: { name: string; image?: string }): Promise<ServiceCategory> => {
    const response = await api.post('appointments/categories-santiago/', categoryData);
    return response.data;
  },

  // Actualizar una categoría de servicio en Santiago
  updateCategorySantiago: async (id: number, categoryData: { name?: string; image?: string | null }): Promise<ServiceCategory> => {
    console.log('[appointmentService.updateCategorySantiago] Enviando:', id, categoryData);
    const response = await api.patch(`appointments/categories-santiago/${id}/`, categoryData);
    console.log('[appointmentService.updateCategorySantiago] Respuesta:', response.data);
    return response.data;
  },

  // Eliminar una categoría de servicio en Santiago
  deleteCategorySantiago: async (id: number): Promise<void> => {
    await api.delete(`appointments/categories-santiago/${id}/`);
  },

  // --- SERVICIOS ---

  // Crear un nuevo servicio
  createService: async (serviceData: Omit<Service, 'id'>): Promise<Service> => {
    const response = await api.post('appointments/services/', serviceData);
    return response.data;
  },

  // Actualizar un servicio
  updateService: async (id: number, serviceData: Partial<Omit<Service, 'id'>>): Promise<Service> => {
    const response = await api.patch(`appointments/services/${id}/`, serviceData);
    return response.data;
  },

  // Eliminar un servicio
  deleteService: async (id: number): Promise<void> => {
    await api.delete(`appointments/services/${id}/`);
  },

  // --- RESERVAS / BOOKING ENDPOINTS ---
  // Obtener franjas ya tomadas (desde backend) para una fecha, opcionalmente filtradas por profesional
  getAvailableSlots: async (date: string, professionalId?: number): Promise<{ start: string; end: string }[]> => {
    let url = `appointments/bookings/available_slots/?date=${date}`;
    if (professionalId) {
      url += `&professional_id=${professionalId}`;
    }
    const response = await api.get(url);
    return response.data || [];
  },

  // Crear una reserva (booking). Payload must include: date, start_time, end_time, services: [ids], optionally customer_name/customer_phone/customer_email
  createBooking: async (payload: any): Promise<any> => {
    const response = await api.post('appointments/bookings/', payload);
    return response.data;
  },

  // --- PROFESIONALES ---
  // Obtener todos los profesionales
  getAllProfessionals: async (): Promise<Professional[]> => {
    const response = await api.get('appointments/professionals/');
    const data = response.data;
    if (data == null) return [];
    if (Array.isArray(data)) return data as Professional[];
    if (Array.isArray(data.results)) return data.results as Professional[];
    return [data] as Professional[];
  },

  // Crear un profesional
  createProfessional: async (professionalData: Omit<Professional, 'id'> | FormData): Promise<Professional> => {
    const response = await api.post('appointments/professionals/', professionalData);
    return response.data;
  },

  // Actualizar un profesional
  updateProfessional: async (id: number, professionalData: Partial<Omit<Professional, 'id'>> | FormData): Promise<Professional> => {
    const response = await api.patch(`appointments/professionals/${id}/`, professionalData);
    return response.data;
  },

  // Eliminar un profesional
  deleteProfessional: async (id: number): Promise<void> => {
    await api.delete(`appointments/professionals/${id}/`);
  },

  // --- RESERVAS (APPOINTMENTS) ---
  // Obtener todas las reservas (para panel de admin)
  getAllBookings: async (): Promise<Appointment[]> => {
    const response = await api.get('appointments/bookings/?no_page=true');
    const data = response.data;
    if (data == null) return [];
    if (Array.isArray(data)) return data as Appointment[];
    if (Array.isArray(data.results)) return data.results as Appointment[];
    return [data] as Appointment[];
  },

  // Obtener reservas para calendario (rango de fechas, sin paginación)
  getCalendarBookings: async (startDate?: string, endDate?: string): Promise<Appointment[]> => {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    const response = await api.get('appointments/bookings/calendar/', { params });
    return response.data as Appointment[];
  },

  // Obtener reservas recientes (últimas 24h)
  getRecentBookings: async (): Promise<Appointment[]> => {
    const response = await api.get('appointments/bookings/recent/');
    const data = response.data;
    if (data == null) return [];
    if (Array.isArray(data)) return data as Appointment[];
    if (Array.isArray(data.results)) return data.results as Appointment[];
    return [data] as Appointment[];
  },

  // Obtener historial de reservas (más de 24h)
  getHistoryBookings: async (): Promise<Appointment[]> => {
    const response = await api.get('appointments/bookings/history/');
    const data = response.data;
    if (data == null) return [];
    if (Array.isArray(data)) return data as Appointment[];
    if (Array.isArray(data.results)) return data.results as Appointment[];
    return [data] as Appointment[];
  },

  // Obtener reservas activas (Pendientes o < 48h desde confirmación/cancelación)
  getActiveBookings: async (): Promise<Appointment[]> => {
    const response = await api.get('appointments/bookings/active/');
    const data = response.data;
    if (data == null) return [];
    if (Array.isArray(data)) return data as Appointment[];
    if (Array.isArray(data.results)) return data.results as Appointment[];
    return [data] as Appointment[];
  },

  // Confirmar reserva
  confirmBooking: async (id: number): Promise<void> => {
    await api.post(`appointments/bookings/${id}/confirm/`);
  },

  // Cancelar reserva
  cancelBooking: async (id: number): Promise<void> => {
    await api.post(`appointments/bookings/${id}/cancel/`);
  },

  // Actualizar una reserva
  updateBooking: async (id: number, bookingData: any): Promise<Appointment> => {
    const response = await api.patch(`appointments/bookings/${id}/`, bookingData);
    return response.data;
  },

  // Eliminar una reserva
  deleteBooking: async (id: number): Promise<void> => {
    await api.delete(`appointments/bookings/${id}/`);
  },

  // --- SANTIAGO ENDPOINTS ---

  // Categorías Santiago
  getAllCategoriesSantiago: async (): Promise<ServiceCategory[]> => {
    const response = await api.get('appointments/categories-santiago/');
    const data = response.data;
    if (data == null) return [];
    if (Array.isArray(data)) return data as ServiceCategory[];
    if (Array.isArray(data.results)) return data.results as ServiceCategory[];
    return [data] as ServiceCategory[];
  },

  // Servicios Santiago
  createServiceSantiago: async (serviceData: Omit<Service, 'id'>): Promise<Service> => {
    const response = await api.post('appointments/services-santiago/', serviceData);
    return response.data;
  },

  updateServiceSantiago: async (id: number, serviceData: Partial<Omit<Service, 'id'>>): Promise<Service> => {
    const response = await api.patch(`appointments/services-santiago/${id}/`, serviceData);
    return response.data;
  },

  deleteServiceSantiago: async (id: number): Promise<void> => {
    await api.delete(`appointments/services-santiago/${id}/`);
  },

  // Profesionales Santiago
  getAllProfessionalsSantiago: async (): Promise<Professional[]> => {
    const response = await api.get('appointments/professionals-santiago/');
    const data = response.data;
    if (data == null) return [];
    if (Array.isArray(data)) return data as Professional[];
    if (Array.isArray(data.results)) return data.results as Professional[];
    return [data] as Professional[];
  },

  createProfessionalSantiago: async (professionalData: Omit<Professional, 'id'> | FormData): Promise<Professional> => {
    const response = await api.post('appointments/professionals-santiago/', professionalData);
    return response.data;
  },

  updateProfessionalSantiago: async (id: number, professionalData: Partial<Omit<Professional, 'id'>> | FormData): Promise<Professional> => {
    const response = await api.patch(`appointments/professionals-santiago/${id}/`, professionalData);
    return response.data;
  },

  deleteProfessionalSantiago: async (id: number): Promise<void> => {
    await api.delete(`appointments/professionals-santiago/${id}/`);
  },

  // Reservas Santiago
  getAvailableSlotsSantiago: async (date: string, professionalId?: number): Promise<{ start: string; end: string }[]> => {
    let url = `appointments/bookings-santiago/available_slots/?date=${date}`;
    if (professionalId) {
      url += `&professional_id=${professionalId}`;
    }
    const response = await api.get(url);
    return response.data || [];
  },

  createBookingSantiago: async (payload: any): Promise<any> => {
    const response = await api.post('appointments/bookings-santiago/', payload);
    return response.data;
  },

  getAllBookingsSantiago: async (): Promise<Appointment[]> => {
    const response = await api.get('appointments/bookings-santiago/?no_page=true');
    const data = response.data;
    if (data == null) return [];
    if (Array.isArray(data)) return data as Appointment[];
    if (Array.isArray(data.results)) return data.results as Appointment[];
    return [data] as Appointment[];
  },

  updateBookingSantiago: async (id: number, bookingData: any): Promise<Appointment> => {
    const response = await api.patch(`appointments/bookings-santiago/${id}/`, bookingData);
    return response.data;
  },

  deleteBookingSantiago: async (id: number): Promise<void> => {
    await api.delete(`appointments/bookings-santiago/${id}/`);
  },

  getActiveBookingsSantiago: async (): Promise<Appointment[]> => {
    const response = await api.get('appointments/bookings-santiago/active/');
    const data = response.data;
    if (data == null) return [];
    if (Array.isArray(data)) return data as Appointment[];
    if (Array.isArray(data.results)) return data.results as Appointment[];
    return [data] as Appointment[];
  },

  getHistoryBookingsSantiago: async (): Promise<Appointment[]> => {
    const response = await api.get('appointments/bookings-santiago/history/');
    const data = response.data;
    if (data == null) return [];
    if (Array.isArray(data)) return data as Appointment[];
    if (Array.isArray(data.results)) return data.results as Appointment[];
    return [data] as Appointment[];
  },

  confirmBookingSantiago: async (id: number): Promise<void> => {
    await api.post(`appointments/bookings-santiago/${id}/confirm/`);
  },

  cancelBookingSantiago: async (id: number): Promise<void> => {
    await api.post(`appointments/bookings-santiago/${id}/cancel/`);
  },
};
